/**
 * Won/Lost 確定ダイアログ。
 * カンバンで won/lost 列にカードがドロップされた時に開く。
 * - Won: finalAmount (必須) + memo (任意)
 * - Lost: lostReason (必須) + memo (任意)
 *
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.2 不変原則
 *   「お金の判断は必ず人間がボタンを押す」
 */
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { useMarkDealLost, useMarkDealWon } from '@/hooks/useDeals';
import {
  useDealQuotes,
  useMarkQuoteLost,
  useMarkQuoteWon,
} from '@/hooks/useQuotes';

const LOST_REASON_PRESETS = [
  '価格が合わなかった',
  '他社と契約',
  'タイミングが合わなかった',
  '顧客の検討停止',
  '機能不足',
  'その他',
];

interface Props {
  open: boolean;
  mode: 'won' | 'lost';
  dealId: string | null;
  initialAmount?: number | null;
  /** 紐づける見積書 ID (見積タブの行ボタンから開いた時に渡す)。 */
  quoteId?: string | null;
  /** 表示用の見積書名 (タイトル等)。quoteId と一緒に渡す。 */
  quoteLabel?: string | null;
  /** 対象商談の顧客名。誤ドラッグ防止のため確認文に表示する。 */
  contactName?: string | null;
  onOpenChange: (open: boolean) => void;
}

export function WonLostDialog({
  open,
  mode,
  dealId,
  initialAmount,
  quoteId,
  quoteLabel,
  contactName,
  onOpenChange,
}: Props) {
  const markWon = useMarkDealWon();
  const markLost = useMarkDealLost();
  const markQuoteWon = useMarkQuoteWon();
  const markQuoteLost = useMarkQuoteLost();
  // quoteId が prop で渡されない場合のみ、複数見積を確認するため一覧を取得
  const dealQuotes = useDealQuotes(quoteId ? null : dealId);
  const [finalAmount, setFinalAmount] = useState<string>('');
  const [lostReason, setLostReason] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  /**
   * カンバン DnD 経由で開いた場合に、複数見積から採用する 1 つを選ばせる。
   * 1 件のみなら自動採用 (effective フォールバックは onDealStatusChanged 側にもあるが、
   * UI で明示しておく方が運用上わかりやすい)。
   */
  const [pickedQuoteId, setPickedQuoteId] = useState<string>('');

  useEffect(() => {
    if (open) {
      setFinalAmount(
        initialAmount != null && Number.isFinite(initialAmount)
          ? String(initialAmount)
          : '',
      );
      setLostReason('');
      setMemo('');
      setPickedQuoteId('');
    }
  }, [open, initialAmount]);

  // quoteId 未指定で見積が 1 件だけなら自動選択 / 2 件以上なら明示選択待ち
  useEffect(() => {
    if (!open) return;
    if (quoteId) return;
    const list = dealQuotes.data ?? [];
    if (list.length === 1) {
      setPickedQuoteId(list[0].id);
    }
  }, [open, quoteId, dealQuotes.data]);

  const effectiveQuoteId = quoteId ?? (pickedQuoteId || null);
  const showQuotePicker =
    !quoteId && (dealQuotes.data?.length ?? 0) > 1;
  const singleAutoPicked =
    !quoteId &&
    (dealQuotes.data?.length ?? 0) === 1 &&
    pickedQuoteId === (dealQuotes.data?.[0]?.id ?? '');
  // DnD ルートで見積が 1 件も無い場合の警告 (確定は禁止しないが明示的に知らせる)
  const quotesLoading = !quoteId && dealQuotes.isLoading;
  const noQuotes = !quoteId && dealQuotes.data != null && dealQuotes.data.length === 0;

  const isPending =
    markWon.isPending ||
    markLost.isPending ||
    markQuoteWon.isPending ||
    markQuoteLost.isPending;

  const handleConfirm = async () => {
    if (!dealId) return;
    if (showQuotePicker && !pickedQuoteId) {
      toast.error('採用する見積を選択してください');
      return;
    }
    try {
      if (mode === 'won') {
        const n = Number(finalAmount.replace(/[,¥\s]/g, ''));
        if (!Number.isFinite(n) || n < 0) {
          toast.error('成約金額は0以上の数値で入力してください');
          return;
        }
        if (quoteId) {
          // 個別見積の成約 → markQuoteWon (Quote 単位、複数回呼べる)
          const r = await markQuoteWon.mutateAsync({
            dealId,
            quoteId,
            finalAmountOverride: n,
            memo: memo.trim() || undefined,
          });
          toast.success(
            r.dealNewlyWon
              ? `成約確定: ¥${n.toLocaleString()} (請求書 ${r.invoiceNumber} 起票)`
              : `見積成約を追加: ¥${n.toLocaleString()} (請求書 ${r.invoiceNumber})`,
          );
        } else {
          // Deal 全体の won 化 (カンバン DnD ルート、Deal 単位 1 回のみ)
          await markWon.mutateAsync({
            dealId,
            finalAmount: n,
            memo: memo.trim() || undefined,
            wonQuoteId: effectiveQuoteId,
          });
          toast.success(`成約確定: ¥${n.toLocaleString()}`);
        }
      } else {
        const reason = lostReason.trim();
        if (!reason) {
          toast.error('失注理由を選択または入力してください');
          return;
        }
        if (quoteId) {
          // 個別見積の失注 → markQuoteLost (Quote 単位)
          await markQuoteLost.mutateAsync({
            dealId,
            quoteId,
            lostReason: reason,
            memo: memo.trim() || undefined,
          });
          toast.success(`見積を失注として記録しました`);
        } else {
          // Deal 全体の失注確定
          await markLost.mutateAsync({
            dealId,
            lostReason: reason,
            memo: memo.trim() || undefined,
            lostQuoteId: effectiveQuoteId,
          });
          toast.success('失注を確定しました');
        }
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`確定失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'won' ? '成約確定' : '失注確定'}
          </DialogTitle>
          <DialogDescription>
            {!quoteId && contactName && (
              <span className="block text-[13px] font-medium text-[var(--text-primary)] mb-1">
                {contactName}様を{mode === 'won' ? '成約' : '失注'}
                に移動しますか？
              </span>
            )}
            {mode === 'won'
              ? quoteId
                ? 'この見積を成約として確定すると、該当見積の lineItems から請求書が自動で起票されます。複数見積を出している商談では、それぞれを個別に成約させて複数の請求書を発行できます。'
                : '成約金額を確定すると、自動的に請求書 (invoices) が起票されます。'
              : quoteId
                ? 'この見積のみを失注として記録します。商談自体や他の見積には影響しません。'
                : '失注理由を記録すると closed になり、再開する場合は手動で stage を戻してください。'}
          </DialogDescription>
        </DialogHeader>

        {quoteId && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-xs">
            <div className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">
              対象見積
            </div>
            <div className="text-[var(--text-primary)] font-medium truncate">
              {quoteLabel || quoteId.slice(0, 8)}
            </div>
          </div>
        )}

        {showQuotePicker && (
          <div className="space-y-1">
            <Label htmlFor="wl-quote">
              採用見積 <span className="text-[var(--accent-red)]">*</span>
            </Label>
            <Select
              value={pickedQuoteId}
              onValueChange={(v) => setPickedQuoteId(v)}
            >
              <SelectTrigger id="wl-quote">
                <SelectValue placeholder="どの見積を採用するか選択" />
              </SelectTrigger>
              <SelectContent>
                {(dealQuotes.data ?? []).map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    {q.title || `見積 ${q.id.slice(0, 6)}`}
                    {' — '}
                    ¥{q.totalIncludingTax.toLocaleString('ja-JP')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[var(--text-muted)]">
              この商談には複数の見積書があります。請求書はここで選んだ見積の lineItems からスナップショットされます。
            </p>
          </div>
        )}

        {noQuotes && mode === 'won' && (
          <div className="rounded-md border border-[var(--accent-red)] bg-[var(--bg-elevated)] px-3 py-2 text-xs text-[var(--accent-red)]">
            <div className="font-medium mb-0.5">⚠ 見積が未作成です</div>
            この商談には見積書が1件もありません。このまま確定すると、明細
            (品目・数量・単価) が空の請求書が自動で作成されます。本当によろしいですか？
          </div>
        )}

        {singleAutoPicked && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-xs">
            <div className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">
              採用見積 (自動選択)
            </div>
            <div className="text-[var(--text-primary)] font-medium truncate">
              {dealQuotes.data?.[0]?.title ||
                `見積 ${dealQuotes.data?.[0]?.id.slice(0, 6)}`}
            </div>
          </div>
        )}

        {mode === 'won' ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="wl-amount">成約金額 (税込)</Label>
              <Input
                id="wl-amount"
                type="text"
                inputMode="numeric"
                placeholder="例: 800000"
                value={finalAmount}
                onChange={(e) => setFinalAmount(e.target.value)}
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                半角数値で入力。カンマ・¥は自動で除去されます。
                {initialAmount != null && (
                  <span className="block text-[var(--accent)]">
                    💡 最新見積/期待金額から自動補完済み (編集可能)
                  </span>
                )}
              </p>
            </div>
            <div>
              <Label htmlFor="wl-memo">メモ (任意)</Label>
              <Textarea
                id="wl-memo"
                rows={2}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="補足情報があれば"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>失注理由</Label>
              <Select
                value={
                  LOST_REASON_PRESETS.includes(lostReason) ? lostReason : '__custom__'
                }
                onValueChange={(v) => {
                  if (v === '__custom__') {
                    setLostReason('');
                  } else {
                    setLostReason(v);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="選択" />
                </SelectTrigger>
                <SelectContent>
                  {LOST_REASON_PRESETS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                  <SelectItem value="__custom__">自由入力</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!LOST_REASON_PRESETS.includes(lostReason) && (
              <div>
                <Label htmlFor="wl-reason-text">理由 (自由入力)</Label>
                <Input
                  id="wl-reason-text"
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  placeholder="例: 予算合わず延期"
                />
              </div>
            )}
            <div>
              <Label htmlFor="wl-memo">メモ (任意)</Label>
              <Textarea
                id="wl-memo"
                rows={2}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="次のアクション、再アプローチ予定など"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant={mode === 'won' ? 'success' : 'danger'}
            onClick={handleConfirm}
            disabled={isPending || quotesLoading}
          >
            {isPending
              ? '確定中...'
              : quotesLoading
              ? '見積を確認中...'
              : mode === 'won'
                ? '成約として確定'
                : '失注として確定'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
